/**
 * 작품명 표시용 — `docs/ops/app-i18n-plan.md` §19
 *
 * 작품명은 **고유명사**라 기계 번역하지 않는다. 웹에서 확인한 **공식 현지 제목**만
 * `content_translation` 에 넣는다. 표시할 때만 아래 순서로 떨어진다:
 *
 *   ① 그 언어의 공식 제목  →  ② **공식 영문 제목**  →  ③ 한국어 원문
 *
 * ②가 있는 이유(2026-09-07 대표 지시): 일본어·중국어 공식 제목이 없는 작품이 많다.
 * 그때 한국어 원제를 보여 주는 것보다 국제적으로 통용되는 영문 제목이 알아보기 쉽다.
 * **저장은 언어별 진짜 공식 제목만** 한다 — 폴백은 화면에서만 일어나므로,
 * 나중에 일본어 공식 제목이 생기면 그 행만 넣으면 바로 그게 나온다.
 *
 * 작품은 350건 남짓이고 제목이 짧아, 언어당 한 번만 통째로 읽어 메모리에 둔다.
 * 카드가 수십 개 그려져도 조회는 (영어 폴백 포함) 최대 두 번이다.
 *
 * ⚠️ **화면에 그릴 때만** 쓴다. `video.title` 을 지도(Map) 키·알림 본문·DB 저장에 쓰는 자리는
 * 한국어 원문 그대로 둬야 한다(Home.jsx 의 작품 중복 제거, 브랜드 문의 알림 등).
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useUser } from "./UserContext";
import { fetchContentTranslations, needsContentTranslation, pickContentText } from "../lib/contentTranslation";

const EMPTY = new Map();
const WorkTitleContext = createContext({ own: EMPTY, en: EMPTY });

/**
 * 언제 다시 읽나 — 로그인한 계정(이메일)이 바뀔 때 · 언어가 바뀔 때.
 * 번역 조회(`get_content_translations`)는 로그인 회원만 부를 수 있다(20261005100000). 이 공급자는 로그인 전
 * 화면부터 떠 있으므로, 로그인 전에 한 번만 읽고 말면 로그인 뒤에도 빈 목록이 남는다. 비로그인이면 읽지 않는다.
 */
export function WorkTitleProvider({ children }) {
  const [maps, setMaps] = useState(() => ({ own: EMPTY, en: EMPTY }));
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const { userInfo } = useUser();
  const email = userInfo?.email || "";

  useEffect(() => {
    if (!email || !needsContentTranslation(lang)) {
      setMaps((m) => (m.own === EMPTY && m.en === EMPTY ? m : { own: EMPTY, en: EMPTY }));
      return undefined;
    }
    let alive = true;
    (async () => {
      // 일본어·중국어 화면은 영문 공식 제목도 같이 읽는다(그 언어 제목이 없을 때 쓴다)
      const [own, en] = await Promise.all([
        fetchContentTranslations("licensed_video", null, lang),
        lang === "en" ? Promise.resolve(EMPTY) : fetchContentTranslations("licensed_video", null, "en"),
      ]);
      if (alive) setMaps({ own, en: lang === "en" ? own : en });
    })();
    return () => { alive = false; };
  }, [email, lang]);

  return <WorkTitleContext.Provider value={maps}>{children}</WorkTitleContext.Provider>;
}

/** 그 언어의 공식 제목 → 공식 영문 제목 → 한국어 원문 */
export function pickWorkTitle(maps, id, koTitle) {
  if (!id) return koTitle;
  const own = pickContentText(maps.own, id, "title", null);
  if (own) return own;
  const en = pickContentText(maps.en, id, "title", null);
  return en || koTitle;
}

/** `const workTitle = useWorkTitle(); … <Text>{workTitle(work)}</Text>` */
export function useWorkTitle() {
  const maps = useContext(WorkTitleContext);
  return useCallback((work) => pickWorkTitle(maps, work?.id, work?.title), [maps]);
}

/** 제목 문자열만 있을 때(부모가 이미 꺼내 둔 경우) — id 를 같이 넘긴다. */
export function useWorkTitleById() {
  const maps = useContext(WorkTitleContext);
  return useMemo(() => (id, koTitle) => pickWorkTitle(maps, id, koTitle), [maps]);
}

export default WorkTitleProvider;
